// Case Buddy addendum recipient-entity resolution helpers.
// This module consumes the reviewed Tool55-style addendum path-ending state and builds Tool48-style review/output views.
// Spreadsheet-shaped rows remain the source of truth; resolved entities are stored as review metadata until staged/applied by later tools.

import { CASE_COLUMN_HEADERS, CASE_DEFAULT_COLS, normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';
import { caseBuddyHashString, normalizeCell, normalizeComparisonKey, rowHasMeaningfulData } from './CaseBuddyShared.js';

export const CASE_ADDENDUM_RECIPIENT_ENTITY_REVIEW_ID = 'addendum_recipient_entities';
export const CASE_ADDENDUM_RECIPIENT_ENTITY_REVIEW_FAMILY = 'addendum_prep_48_resolution';
export const CASE_ADDENDUM_RECIPIENT_ENTITY_REVIEW_STATUS_SELECTED = 'selected';
export const CASE_ADDENDUM_RECIPIENT_ENTITY_HEADERS = Object.freeze(CASE_COLUMN_HEADERS.slice(0, CASE_DEFAULT_COLS));

export function normalizeCaseAddendumEntityName(value = '') {
  return normalizeCell(value)
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeCaseAddendumEntityOptions(values = []) {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .flatMap((item) => {
      if (Array.isArray(item)) return item;
      if (typeof item === 'string' && item.includes(',')) return item.split(',');
      return [item];
    })
    .map(item => normalizeCaseAddendumEntityName(item?.name || item?.presentedName || item?.canonicalName || item))
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
}

export function buildCaseAddendumRecipientExcerpt(note = '', term = '') {
  const source = normalizeCell(note);
  const needle = normalizeCaseAddendumEntityName(term);
  if (!source || !needle) return '';
  const sourceLower = source.toLowerCase();
  const needleLower = needle.toLowerCase();
  const index = sourceLower.indexOf(needleLower);
  if (index < 0) return '';
  const radius = 80;
  const start = Math.max(0, index - radius);
  const end = Math.min(source.length, index + needle.length + radius);
  const prefix = start > 0 ? '…' : '';
  const suffix = end < source.length ? '…' : '';
  return `${prefix}${source.slice(start, end)}${suffix}`;
}

export function buildCaseAddendumRecipientExcerpts(note = '', options = []) {
  const seen = new Set();
  return normalizeCaseAddendumEntityOptions(options)
    .map(option => buildCaseAddendumRecipientExcerpt(note, option))
    .filter(Boolean)
    .filter((excerpt) => {
      const key = excerpt.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function buildCaseAddendumRecipientReviewBasisHash(source = {}, options = []) {
  return `addendum-recipient-row-${caseBuddyHashString(JSON.stringify({
    rowId: normalizeCell(source.rowId || source.key),
    sourceRowNumber: Number(source.sourceRowNumber || source.sheetRowNumber) || 0,
    pathLabel: normalizeCell(source.pathLabel || source.effectivePathLabel),
    transactionHash: normalizeCell(source.transactionHash || source.effectiveTransactionHash),
    fromAddress: normalizeCell(source.fromAddress || source.effectiveFromAddress),
    toAddress: normalizeCell(source.toAddress),
    utcDate: normalizeCell(source.utcDate || source.dateUtc),
    amount: normalizeCell(source.amount),
    token: normalizeCell(source.token || source.tokenSymbol),
    fundsFollowed: normalizeCell(source.fundsFollowed),
    notes: normalizeCell(source.notes),
    options: normalizeCaseAddendumEntityOptions(options)
  }))}`;
}

export function buildCaseAddendumRecipientReviewKey(caseId = '', rowId = '', reviewBasisHash = '') {
  return [
    normalizeComparisonKey(caseId || 'case'),
    CASE_ADDENDUM_RECIPIENT_ENTITY_REVIEW_FAMILY,
    normalizeComparisonKey(rowId),
    normalizeComparisonKey(reviewBasisHash)
  ].join('|');
}

function getSavedDecisionForCandidate(workflowState = {}, reviewKey = '') {
  const review = workflowState?.addendumRecipientEntityReview || null;
  const decisions = review?.decisionsByReviewKey && typeof review.decisionsByReviewKey === 'object'
    ? review.decisionsByReviewKey
    : (review?.decisionsByKey && typeof review.decisionsByKey === 'object' ? review.decisionsByKey : {});
  return reviewKey ? (decisions[reviewKey] || null) : null;
}

export function buildCaseAddendumRecipientCandidate(source = {}, workflowState = {}, options = {}) {
  const candidateOptions = normalizeCaseAddendumEntityOptions(source.matchedKeywords || source.candidateEntityNames || source.matchedKeywordsText || []);
  const rowId = normalizeCell(source.rowId || source.key || `addendum-recipient-row-${source.sourceRowNumber || source.sourceRowIndex || 0}`);
  const reviewBasisHash = buildCaseAddendumRecipientReviewBasisHash({ ...source, rowId }, candidateOptions);
  const reviewKey = buildCaseAddendumRecipientReviewKey(options.caseId || 'case', rowId, reviewBasisHash);
  const savedDecision = getSavedDecisionForCandidate(workflowState, reviewKey);
  const savedSelectionRaw = normalizeCaseAddendumEntityName(savedDecision?.selectedEntityName || savedDecision?.selectedEntity || savedDecision?.resolvedEntityName || savedDecision?.attributionValue || '');
  let resolvedEntityName = '';
  let isAutoResolved = false;
  let hasSavedSelection = false;

  if (candidateOptions.length === 1) {
    resolvedEntityName = candidateOptions[0];
    isAutoResolved = true;
  } else if (savedSelectionRaw) {
    const matchedOption = candidateOptions.find(option => option.toLowerCase() === savedSelectionRaw.toLowerCase());
    if (matchedOption) {
      resolvedEntityName = matchedOption;
      hasSavedSelection = true;
    }
  }

  const normalizedOutputRow = normalizeCaseMatrix([source.outputRow || []], 1, CASE_DEFAULT_COLS)[0].slice(0, CASE_DEFAULT_COLS);
  const pathLabel = normalizeCell(source.pathLabel || normalizedOutputRow[0]);
  const transactionHash = normalizeCell(source.transactionHash || normalizedOutputRow[1]);
  const fromAddress = normalizeCell(source.fromAddress || normalizedOutputRow[2]);
  const toAddress = normalizeCell(source.toAddress || normalizedOutputRow[3]);
  const utcDate = normalizeCell(source.utcDate || normalizedOutputRow[4]);
  const amount = normalizeCell(source.amount || normalizedOutputRow[5]);
  const token = normalizeCell(source.token || normalizedOutputRow[6]);
  const notes = normalizeCell(source.notes || normalizedOutputRow[9]);
  const fundsFollowed = normalizeCell(source.fundsFollowed || normalizedOutputRow[12]);
  const matchedSourceTerms = normalizeCaseAddendumEntityOptions(source.matchedSourceTerms || source.matchedKeywords || source.matchedKeywordsText || candidateOptions);
  const matchedExcerpts = Array.isArray(source.matchedExcerpts) && source.matchedExcerpts.length
    ? source.matchedExcerpts.map(normalizeCell).filter(Boolean)
    : buildCaseAddendumRecipientExcerpts(notes, matchedSourceTerms);

  const hasNoCandidateOptions = candidateOptions.length === 0;
  const needsUserChoice = candidateOptions.length > 1 && !resolvedEntityName;
  const status = resolvedEntityName
    ? (isAutoResolved ? 'auto_resolved' : 'selected')
    : (hasNoCandidateOptions ? 'no_options' : 'needs_choice');
  const statusLabel = resolvedEntityName
    ? (isAutoResolved ? 'Auto-resolved' : 'Selected')
    : (hasNoCandidateOptions ? 'No options' : 'Needs choice');

  const candidate = {
    key: `${rowId}|${reviewBasisHash}`,
    reviewKey,
    reviewBasisHash,
    rowId,
    sourceRowIndex: Number(source.sourceRowIndex),
    sourceRowNumber: Number(source.sourceRowNumber || source.sheetRowNumber) || 0,
    effectivePathLabel: pathLabel,
    effectiveTransactionHash: transactionHash,
    effectiveFromAddress: fromAddress,
    toAddress,
    dateUtc: utcDate,
    amount,
    tokenSymbol: token,
    fundsFollowed,
    notes,
    candidateEntityNames: candidateOptions,
    candidateEntityNamesText: candidateOptions.join(', '),
    matchedSourceTerms,
    matchedSourceTermsText: matchedSourceTerms.join(', '),
    matchedExcerpts,
    isAutoResolved,
    needsUserChoice,
    hasNoCandidateOptions,
    status,
    statusLabel,
    hasSavedSelection,
    resolvedEntityName,
    currentReviewStatus: hasSavedSelection ? CASE_ADDENDUM_RECIPIENT_ENTITY_REVIEW_STATUS_SELECTED : '',
    existingDecision: savedDecision,
    outputRow: normalizedOutputRow,
    searchText: [
      rowId,
      source.sourceRowNumber,
      pathLabel,
      transactionHash,
      fromAddress,
      toAddress,
      utcDate,
      amount,
      token,
      fundsFollowed,
      notes,
      resolvedEntityName,
      candidateOptions.join(' '),
      matchedSourceTerms.join(' '),
      matchedExcerpts.join(' ')
    ].map(value => normalizeCell(value).toLowerCase()).join(' ')
  };

  return candidate;
}

export function buildCaseAddendumRecipientCurrentBasisHash(candidates = []) {
  const rows = (Array.isArray(candidates) ? candidates : [])
    .map(candidate => [candidate.reviewKey, candidate.reviewBasisHash, candidate.resolvedEntityName])
    .sort((left, right) => normalizeCell(left[0]).localeCompare(normalizeCell(right[0]), undefined, { sensitivity: 'base' }));
  return `addendum-recipient-review-${caseBuddyHashString(JSON.stringify(rows))}`;
}

export function buildCaseAddendumRecipientEntityState(addendumPathEndingState = {}, workflowState = {}, options = {}) {
  const sourceRows = Array.isArray(addendumPathEndingState?.includedRows) ? addendumPathEndingState.includedRows : [];
  const caseId = options.caseId || 'case';
  const candidates = sourceRows
    .map(source => buildCaseAddendumRecipientCandidate(source, workflowState, { caseId }))
    .sort((left, right) => {
      const leftResolved = !!left.resolvedEntityName;
      const rightResolved = !!right.resolvedEntityName;
      if (leftResolved !== rightResolved) return leftResolved ? 1 : -1;
      const rowCompare = (Number(left.sourceRowNumber) || Number.MAX_SAFE_INTEGER) - (Number(right.sourceRowNumber) || Number.MAX_SAFE_INTEGER);
      if (rowCompare) return rowCompare;
      return normalizeCell(left.effectivePathLabel).localeCompare(normalizeCell(right.effectivePathLabel), undefined, { sensitivity: 'base' });
    });

  const totalCandidateCount = candidates.length;
  const autoResolvedCount = candidates.filter(candidate => candidate.isAutoResolved).length;
  const resolvedCount = candidates.filter(candidate => !!candidate.resolvedEntityName).length;
  const needsChoiceCount = candidates.filter(candidate => candidate.needsUserChoice).length;
  const multiMatchCount = candidates.filter(candidate => (candidate.candidateEntityNames || []).length > 1).length;
  const noOptionCount = candidates.filter(candidate => (candidate.candidateEntityNames || []).length === 0).length;
  const pathEndingReviewIsCurrent = !!addendumPathEndingState?.reviewIsCurrent;
  const pathEndingReviewCanReview = !!addendumPathEndingState?.canReview;
  const buildIsCurrent = pathEndingReviewIsCurrent;
  const currentReviewBasisHash = buildCaseAddendumRecipientCurrentBasisHash(candidates);
  const review = workflowState?.addendumRecipientEntityReview || null;
  const caseDataRevision = Number(options.caseDataRevision) || 0;
  const confirmedReviewBasisHash = normalizeCell(review?.basisHash);
  const reviewIsCurrent = buildIsCurrent && needsChoiceCount <= 0 && noOptionCount <= 0 && !!(review?.confirmedAtUtc || review?.reviewedAtUtc) && confirmedReviewBasisHash === currentReviewBasisHash && Number(review?.revision) === caseDataRevision;

  let summary = `${resolvedCount}/${totalCandidateCount} addendum candidate row${totalCandidateCount === 1 ? '' : 's'} have a resolved recipient entity.`;
  if (autoResolvedCount > 0) summary += ` ${autoResolvedCount} row${autoResolvedCount === 1 ? '' : 's'} auto-resolved because only one entity matched.`;
  if (needsChoiceCount > 0) summary += ` ${needsChoiceCount} row${needsChoiceCount === 1 ? '' : 's'} still need a user choice.`;
  if (noOptionCount > 0) summary += ` ${noOptionCount} row${noOptionCount === 1 ? '' : 's'} have no candidate entity options.`;
  if (!pathEndingReviewCanReview) summary = addendumPathEndingState?.summary || 'Review exchange path-ending rows before resolving addendum recipient entities.';
  else if (!pathEndingReviewIsCurrent) summary += ' The addendum path-ending review is not current yet.';
  summary += ` Review ${reviewIsCurrent ? 'is current.' : 'is not current.'}`;

  return {
    candidates,
    totalCandidateCount,
    autoResolvedCount,
    resolvedCount,
    needsChoiceCount,
    multiMatchCount,
    noOptionCount,
    noCandidateEntityCount: noOptionCount,
    pathEndingReviewIsCurrent,
    pathEndingReviewCanReview,
    buildIsCurrent,
    canConfirm: buildIsCurrent && needsChoiceCount <= 0 && noOptionCount <= 0 && totalCandidateCount > 0,
    currentReviewBasisHash,
    confirmedReviewBasisHash,
    reviewIsCurrent,
    confirmedAtUtc: review?.confirmedAtUtc || review?.reviewedAtUtc || '',
    reviewedAtUtc: review?.reviewedAtUtc || review?.confirmedAtUtc || '',
    reviewedRevision: Number(review?.revision),
    caseDataRevision,
    pathEndingBasisHash: addendumPathEndingState?.currentBasisHash || '',
    sourceIncludedCount: addendumPathEndingState?.includedCount || 0,
    summary
  };
}

export function buildCaseAddendumRecipientResolvedOutputRows(state = {}) {
  const rows = (Array.isArray(state.candidates) ? state.candidates : [])
    .filter(candidate => !!candidate.resolvedEntityName)
    .map((candidate) => {
      const row = normalizeCaseMatrix([candidate.outputRow || []], 1, CASE_DEFAULT_COLS)[0].slice(0, CASE_DEFAULT_COLS);
      row[10] = candidate.resolvedEntityName;
      return row;
    });
  return [CASE_ADDENDUM_RECIPIENT_ENTITY_HEADERS.slice(), ...rows];
}

export function buildCaseAddendumRecipientResolvedRows(state = {}) {
  return (Array.isArray(state.candidates) ? state.candidates : [])
    .filter(candidate => !!candidate.resolvedEntityName)
    .map(candidate => ({
      rowId: candidate.rowId,
      sourceRowNumber: candidate.sourceRowNumber,
      effectivePathLabel: candidate.effectivePathLabel,
      effectiveTransactionHash: candidate.effectiveTransactionHash,
      resolvedEntityName: candidate.resolvedEntityName,
      toAddress: candidate.toAddress,
      notes: candidate.notes
    }));
}

export function buildCaseAddendumRecipientAuditRows(state = {}, actionLabel = 'Addendum recipient entity review') {
  const candidates = Array.isArray(state.candidates) ? state.candidates : [];
  return [
    ['Case Buddy Addendum Recipient Entity Review Audit', '', '', '', '', '', '', '', '', 'Audit only. Built from reviewed addendum path-ending rows; Main Data is not modified.'],
    ['Action', actionLabel, 'Basis', state.currentReviewBasisHash || '', 'Review Current', state.reviewIsCurrent ? 'Yes' : 'No'],
    ['Candidates', state.totalCandidateCount || 0, 'Resolved', state.resolvedCount || 0, 'Need Choice', state.needsChoiceCount || 0, 'Auto Resolved', state.autoResolvedCount || 0],
    ['Path-ending Review Current', state.pathEndingReviewIsCurrent ? 'Yes' : 'No', 'Path-ending Basis', state.pathEndingBasisHash || '', 'Case Revision', state.caseDataRevision || 0],
    [],
    ['Status', 'WWW Row', 'Path Label', 'Transaction Hash', 'To Address', 'Resolved Entity', 'Candidate Entities', 'Matched Source Terms', 'Column J Notes'],
    ...candidates.map(candidate => [
      candidate.resolvedEntityName
        ? candidate.isAutoResolved ? 'Auto resolved' : 'Resolved'
        : (candidate.candidateEntityNames || []).length > 1 ? 'Needs user choice' : 'No candidate options',
      candidate.sourceRowNumber || '',
      candidate.effectivePathLabel || '',
      candidate.effectiveTransactionHash || '',
      candidate.toAddress || '',
      candidate.resolvedEntityName || '',
      candidate.candidateEntityNamesText || '',
      candidate.matchedSourceTermsText || '',
      candidate.notes || ''
    ])
  ].filter(row => Array.isArray(row) ? rowHasMeaningfulData(row) || row.length === 0 : true);
}

// Compatibility aliases for the Options API method bundle.
export const buildCaseAddendumRecipientEntityReviewState = buildCaseAddendumRecipientEntityState;
export const buildCaseAddendumRecipientEntityOutputRows = buildCaseAddendumRecipientResolvedOutputRows;
export const buildCaseAddendumRecipientEntityAuditRows = buildCaseAddendumRecipientAuditRows;
